import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  getNavReconOverview,
  getNavReconOverviewExpanded,
  getNavReconPipelineMetadata,
  getNavReconPositions,
  getNavReconShareClasses,
} from '../nav-recon.routes';
import {
  NavReconFundPositions,
  NavReconFundShareClasses,
  NavReconOverview2Response,
  NavReconOverviewObject,
  NavReconPipelineMetadata,
} from '../types';

/**
 * Poll interval while NAV Recon queries are observed (default: once per minute).
 * Override per hook via `options` (e.g. `refetchInterval: false` to disable).
 */
export const NAV_RECON_POLL_INTERVAL_MS = 60_000;

const navReconLiveQueryDefaults = {
  staleTime: 0,
  refetchInterval: NAV_RECON_POLL_INTERVAL_MS,
  // Override global placeholderData: (prev) => prev in queryClient.ts
  placeholderData: () => undefined,
} as const;

/** Fund detail pages (positions, share classes) — no background polling. */
const navReconFundDetailQueryDefaults = {
  refetchInterval: false,
  placeholderData: () => undefined,
} as const;

/**
 * Query hook for the NAV-Recon overview (expanded route) — `get_nav_recon_overview`.
 */
export const useNavReconOverviewExpanded = (
  positionDate: string | undefined,
  options?: Omit<
    UseQueryOptions<NavReconOverviewObject[], Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) =>
  useQuery({
    queryKey: ['nav-recon-overview-expanded', positionDate],
    queryFn: () => getNavReconOverviewExpanded(positionDate!),
    enabled: !!positionDate,
    ...navReconLiveQueryDefaults,
    ...options,
  });

/**
 * Query hook for the default NAV-Recon overview — `get_nav_recon_overview2`.
 */
export const useNavReconOverview = (
  positionDate: string | undefined,
  options?: Omit<
    UseQueryOptions<NavReconOverview2Response, Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) =>
  useQuery({
    queryKey: ['nav-recon-overview', positionDate],
    queryFn: () => getNavReconOverview(positionDate!),
    enabled: !!positionDate,
    ...navReconLiveQueryDefaults,
    ...options,
  });

export const useNavReconPipelineMetadata = (
  positionDate: string | undefined,
  fundName?: string,
  options?: Omit<
    UseQueryOptions<NavReconPipelineMetadata[], Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) =>
  useQuery({
    queryKey: ['nav-recon-pipeline-metadata', positionDate, fundName ?? null],
    queryFn: () => getNavReconPipelineMetadata(positionDate!, fundName),
    enabled: !!positionDate,
    ...navReconLiveQueryDefaults,
    ...options,
  });

export const useNavReconShareClasses = (
  positionDate: string | undefined,
  fundName: string | undefined,
  options?: Omit<
    UseQueryOptions<NavReconFundShareClasses[], Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) =>
  useQuery({
    queryKey: ['nav-recon-share-classes', positionDate, fundName],
    queryFn: () => getNavReconShareClasses(positionDate!, fundName!),
    enabled: !!positionDate && !!fundName,
    ...navReconFundDetailQueryDefaults,
    ...options,
  });

export const useNavReconPositions = (
  positionDate: string | undefined,
  fundName: string | undefined,
  options?: Omit<
    UseQueryOptions<NavReconFundPositions[], Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) =>
  useQuery({
    queryKey: ['nav-recon-positions', positionDate, fundName],
    queryFn: () => getNavReconPositions(positionDate!, fundName!),
    enabled: !!positionDate && !!fundName,
    ...navReconFundDetailQueryDefaults,
    ...options,
  });
